<script setup lang="ts">
/**
 * 色板页签 —— 对齐参考产品（svgeditai.com）的工作流：
 *   自动提取颜色（带元素计数） → 逐行映射到品牌色 → 一键应用映射（一条可撤销命令）
 *
 * 交互约定：
 *  · **悬停**颜色行 = 画布上预览（虚线框住命中元素 + 其余压暗），**不动选中集**
 *  · **点行右侧的目标色块/箭头** = 弹出悬浮框：品牌色板 + 自定义颜色
 *  · **选色立刻写入文档**（一行一次、可 Ctrl+Z 逐条撤回；连续调色合并成一条）
 *  · 单栏列表；品牌色**只在这里的悬浮框里用**，列表底部「上传 JSON」是唯一的加色入口
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useEditor } from '@/composables/useEditor';
import { brandColorsFromText, COLOR_PRESETS, roleLabel, type ColorUsage } from '@/core/palette';
import { setInspectorTab } from '@/stores/editor';
import { addBrandColor, addBrandColors, brandColors, clearMapping, palette, setHint, setMapping } from '@/stores/palette';

const editor = useEditor();

/**
 * 已映射源色的"冻结"快照。
 * 为什么需要：颜色一被改写，文档里就没有这个色了 —— 若列表照实时提取，用户点完色
 * 那一行会当场消失（像丢了东西）。所以映射过的源色保留它原来的计数与角色继续显示。
 */
const frozen = ref<Record<string, ColorUsage>>({});

/** 列表 = ①已映射的源色（含已从文档消失的） + ②文档里当前的颜色（排除映射的目标色） */
const usages = computed<ColorUsage[]>(() => {
  const live = editor.colorUsages.value;
  const targets = new Set(Object.values(palette.mapping));
  const out: ColorUsage[] = [];
  const seen = new Set<string>();
  for (const hex of Object.keys(palette.mapping)) {
    const snap = frozen.value[hex] ?? live.find((u) => u.hex === hex);
    if (snap && !seen.has(snap.hex)) {
      out.push(snap);
      seen.add(snap.hex);
    }
  }
  for (const u of live) {
    if (seen.has(u.hex) || targets.has(u.hex)) continue;
    out.push(u);
    seen.add(u.hex);
  }
  return out.sort((a, b) => b.count - a.count);
});

/** 冻结某一行的快照（选色时调用：此刻文档里还有这个色，计数最准） */
const freeze = (hex: string): void => {
  const snap = usages.value.find((u) => u.hex === hex);
  if (snap) frozen.value = { ...frozen.value, [hex]: snap };
};
const mappedCount = computed(() => Object.keys(palette.mapping).length);
const canApply = computed(() => mappedCount.value > 0 && usages.value.length > 0);

const customHex = ref('#000000');
/** 悬浮框宽度：一行要放得下「组名 + 6 个色格」 */
const POP_W = 300;
/** 估算高度：只为"下方放不下就向上弹"，取 CSS 里 max-height 的上限（最坏情况也算得准） */
const POP_H = 420;
/** 悬浮框位置（fixed 定位，Teleport 到 body，避免被面板 overflow 裁掉） */
const popAt = ref({ left: 0, top: 0, width: POP_W });

const targetOf = (hex: string): string | null => palette.mapping[hex] ?? null;

/** 悬停颜色行：临时预览（画布虚线框 + 其余压暗），选中集原封不动 */
const hover = (hex: string | null): void => {
  palette.hover = hex;
  editor.previewColor(hex);
};

/** 点行右侧目标色块/箭头 → 开/关悬浮框（贴触发器右缘，下方放不下则向上弹） */
const togglePicker = (hex: string, e: MouseEvent): void => {
  if (palette.pickerFor === hex) {
    palette.pickerFor = null;
    return;
  }
  const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
  // 宽高都先按视口夹一次：贴着窗口边缘的触发器不能让弹层溢出屏幕
  const w = Math.min(POP_W, window.innerWidth - 16);
  const h = Math.min(POP_H, window.innerHeight - 16);
  const below = r.bottom + 6;
  const top = below + h > window.innerHeight - 8 ? r.top - h - 6 : below;
  popAt.value = {
    left: Math.max(8, Math.min(r.right - w, window.innerWidth - w - 8)),
    top: Math.max(8, Math.min(top, window.innerHeight - h - 8)),
    width: w,
  };
  customHex.value = targetOf(hex) ?? hex;
  palette.pickerFor = hex;
};

/**
 * 点品牌色：立刻写入文档，但**悬浮框保持打开** ——
 * 选色即写入之后，连着点几个色看画布变化才是正常用法（一点就收起来很难受，用户反馈过）。
 * 关闭靠：点外部 / Esc / 再点一次那个触发器 / 点「应用映射」。
 */
const choose = (hex: string): void => {
  const src = palette.pickerFor;
  if (!src) return;
  freeze(src);
  setMapping(src, hex);
  const ok = editor.pickColorMapping(src, hex);
  setHint(`${src} → ${hex}${ok ? '（已写入，Ctrl+Z 可撤回）' : '（文档里已无此色，仅记录）'}`);
};

const onCustom = (e: Event): void => {
  const hex = (e.target as HTMLInputElement).value;
  customHex.value = hex;
  const src = palette.pickerFor;
  if (!src) return;
  freeze(src);
  setMapping(src, hex);
  // 连续拖动取色 → merge 合并成一条历史，松手只占一次 Ctrl+Z
  editor.pickColorMapping(src, hex, { merge: true });
  setHint(`${src} → ${hex}（自定义 · 实时写入）`);
};

const clearRow = (): void => {
  const src = palette.pickerFor;
  if (!src) return;
  palette.pickerFor = null;
  // 已经写进文档的：按原字面量还回去（一条可撤销命令），再把这一行放回实时提取
  const reverted = editor.revertColorPick(src);
  setMapping(src, null);
  if (reverted) {
    const next = { ...frozen.value };
    delete next[src];
    frozen.value = next;
  }
  setHint(reverted ? `${src} 已改回原色，映射已清除` : `已清除 ${src} 的映射`);
};

/* 关闭时机：点外部 / Esc（捕获阶段拦下，别让编辑器的 Esc 顺手清掉选中）*/
const onDocDown = (e: Event): void => {
  const t = e.target;
  // e.target 不一定是 Element（document/window 也会派发事件）—— 不能直接 .closest
  if (t instanceof Element && (t.closest('.pal-pop') || t.closest('[data-field="pal-target"]'))) return;
  palette.pickerFor = null;
};
const onDocKey = (e: KeyboardEvent): void => {
  if (e.key !== 'Escape') return;
  e.stopPropagation();
  palette.pickerFor = null;
};

watch(
  () => palette.pickerFor,
  (open) => {
    if (open) {
      document.addEventListener('pointerdown', onDocDown, true);
      document.addEventListener('keydown', onDocKey, true);
    } else {
      document.removeEventListener('pointerdown', onDocDown, true);
      document.removeEventListener('keydown', onDocKey, true);
    }
  },
);
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocDown, true);
  document.removeEventListener('keydown', onDocKey, true);
});

const apply = (selectionOnly: boolean): void => {
  palette.menuOpen = false;
  palette.pickerFor = null;
  editor.applyColorMapping({ selectionOnly });
};

const clear = (): void => {
  clearMapping();
  palette.pickerFor = null;
  frozen.value = {};
  setHint('已清除映射表（文档改动请用 Ctrl+Z 撤回）');
};

const close = (): void => {
  setInspectorTab('props');
};

/* ------------------------------------------------------------ 手动添加品牌色
 * 与「上传 JSON」并列的另一条路：取色 → 写名字 → ＋。
 * 同色值不重复堆叠（和 JSON 导入一个口径），免得选色悬浮框里出现两个一样的色块。
 */
const newHex = ref('#6366f1');
const newLabel = ref('');

const onNewHex = (e: Event): void => {
  newHex.value = (e.target as HTMLInputElement).value;
};

const add = (): void => {
  const hex = newHex.value;
  if (brandColors().some((c) => c.hex.toLowerCase() === hex.toLowerCase())) {
    setHint(`${hex} 已在品牌色板里`);
    return;
  }
  addBrandColor(newLabel.value.trim() || hex, hex);
  newLabel.value = '';
  setHint(`已添加品牌色 ${hex}`);
};

/* ------------------------------------------------------------ 上传配色文件 + 粘贴多个 HEX
 * 两条路都进 `addBrandColors`（按色值去重、追加不覆盖），认色规则全在 core/palette 的纯函数里：
 *  · 文件：原来只认 .json（Figma 变量 / 设计令牌），现在同一入口也吃 .css / .txt —— 按**内容**分流，
 *    JSON 能 parse 就走 JSON 口径，否则当自由文本认（`--brand-500: #6366f1;` 这种）。
 *  · 粘贴：一串 HEX（空格 / 逗号 / 换行都行）。**边打边认**，下面实时显示认到了几个色 ——
 *    用户先看到「识别到 N 个颜色」再点导入，不会出现「点了没反应」的猜谜。
 */
const fileInput = ref<HTMLInputElement | null>(null);
const pickFile = (): void => fileInput.value?.click();

/** 读文件文本：用 FileReader 而不是 `file.text()`（后者在老浏览器与 jsdom 里都没有） */
const readText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsText(file);
  });

/** 一次导入的收尾：落地 + 一句结果（新增几个、跳过几个都在这一句里） */
const importColors = (colors: ReturnType<typeof brandColorsFromText>, source: string): void => {
  const added = addBrandColors(colors);
  const skipped = colors.length - added;
  if (added === 0) setHint(`${source}的 ${colors.length} 个颜色都已经在品牌色板里了`);
  else setHint(`${source}：已添加 ${added} 个品牌色${skipped > 0 ? `（${skipped} 个已在色板里）` : ''}`);
};

const onFile = async (e: Event): Promise<void> => {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0] ?? null;
  input.value = ''; // 同一个文件连选两次也要能再次触发
  if (!file) return;
  let colors: ReturnType<typeof brandColorsFromText> = [];
  try {
    colors = brandColorsFromText(await readText(file));
  } catch {
    setHint(`${file.name} 读不出来`);
    return;
  }
  if (colors.length === 0) {
    setHint(`${file.name} 里没有找到颜色（HEX / rgb() / CSS 变量 / 设计令牌 JSON 都认）`);
    return;
  }
  importColors(colors, file.name);
};

/* ---- 粘贴多个 HEX：边打边认，先给预览再落盘 ---- */
const pasteOpen = ref(false);
const pasteText = ref('');
/** 实时识别的结果（纯函数，输入即结果 —— 面板不自己解析） */
const pasteFound = computed(() => brandColorsFromText(pasteText.value));

/** 预览里最多铺多少个色块（再多就只是数字了） */
const PREVIEW_MAX = 24;

const togglePaste = (): void => {
  pasteOpen.value = !pasteOpen.value;
  if (!pasteOpen.value) pasteText.value = '';
};

const savePaste = (): void => {
  const colors = pasteFound.value;
  if (colors.length === 0) return;
  importColors(colors, '粘贴');
  pasteOpen.value = false;
  pasteText.value = '';
};
</script>

<template>
  <div class="pal">
    <div class="pal-head">
      <span class="pal-icon" aria-hidden="true">🎨</span>
      <b>色板</b>
      <span class="panel-note">· 从你的 SVG 自动提取</span>
      <span class="grow" />
      <button class="ghost-btn" data-field="pal-clear" :disabled="mappedCount === 0" @click="clear">清除</button>
      <div class="pal-apply">
        <button class="primary-btn" data-field="pal-apply" :disabled="!canApply" @click="apply(false)">重新应用</button>
        <button
          class="primary-btn pal-caret"
          title="更多方式"
          :disabled="!canApply"
          @click="palette.menuOpen = !palette.menuOpen"
        >
          ▾
        </button>
        <div v-if="palette.menuOpen" class="pal-menu">
          <button data-field="pal-apply-all" @click="apply(false)">应用到整篇文档</button>
          <button data-field="pal-apply-sel" :disabled="editor.selection.value.length === 0" @click="apply(true)">
            仅应用到选中元素（{{ editor.selection.value.length }}）
          </button>
        </div>
      </div>
      <button class="ghost-btn" title="关闭色板" @click="close">✕</button>
    </div>

    <section class="pal-col">
      <div class="pal-colhead">
        <span>SVG 中的颜色</span>
        <span class="grow" />
        <span class="chip" data-field="pal-count">{{ usages.length }} 种</span>
      </div>
      <div class="pal-list">
        <div
          v-for="usage in usages"
          :key="usage.hex"
          class="pal-row"
          :class="{ 'is-preview': palette.hover === usage.hex, 'is-mapped': targetOf(usage.hex) !== null }"
          data-field="pal-row"
          :data-hex="usage.hex"
          :title="`${usage.hex} · ${usage.count} 个元素 · ${roleLabel(usage)}`"
          @mouseenter="hover(usage.hex)"
          @mouseleave="hover(null)"
        >
          <span class="pal-swatch" :style="{ background: usage.hex }" />
          <span class="pal-meta">
            <b>{{ usage.hex }}</b>
            <i>{{ usage.count }} 个元素</i>
          </span>
          <button
            class="pal-pick"
            :class="{ 'is-open': palette.pickerFor === usage.hex }"
            data-field="pal-target"
            :title="targetOf(usage.hex) ? `目标 ${targetOf(usage.hex)}：点击换一个颜色` : '点击选择颜色（品牌色板 / 自定义）'"
            @click.stop="togglePicker(usage.hex, $event)"
            @focus="hover(usage.hex)"
            @blur="hover(null)"
          >
            <span class="pal-arrow" aria-hidden="true">→</span>
            <span
              class="pal-target"
              :class="{ 'is-empty': targetOf(usage.hex) === null }"
              :style="targetOf(usage.hex) ? { background: targetOf(usage.hex) as string } : {}"
            />
          </button>
        </div>
        <p v-if="usages.length === 0" class="empty-hint">文档里还没有可映射的颜色</p>
      </div>

      <!-- 两条加品牌色的路：先手动取色写名字，或者整包上传 JSON -->
      <div class="pal-addcolor">
        <input
          class="pal-newswatch"
          type="color"
          :value="newHex"
          aria-label="品牌色颜色"
          data-field="pal-new-hex"
          @input="onNewHex"
        />
        <div class="input">
          <input
            data-field="pal-new-label"
            placeholder="颜色名称"
            spellcheck="false"
            :value="newLabel"
            @input="newLabel = ($event.target as HTMLInputElement).value"
            @keydown.enter.prevent="add"
          />
        </div>
        <button class="pal-plus" data-field="pal-add" title="添加品牌色" @click="add">＋</button>
      </div>

      <button
        class="pal-addjson"
        data-field="pal-add-json"
        title="Figma 变量 / 设计令牌 JSON、CSS 变量、或一份纯 HEX 清单"
        @click="pickFile"
      >
        <span class="pal-addicon" aria-hidden="true">＋</span>
        上传配色文件
      </button>
      <input
        ref="fileInput"
        class="pal-file"
        type="file"
        accept=".json,.css,.txt,.md,application/json,text/css,text/plain"
        data-field="pal-json-file"
        @change="onFile"
      />

      <button
        class="pal-addjson"
        data-field="pal-add-text"
        :class="{ 'is-open': pasteOpen }"
        title="一串 HEX、一段 CSS、或一份 JSON 原文"
        @click="togglePaste"
      >
        <span class="pal-addicon" aria-hidden="true">＋</span>
        粘贴多个 HEX
      </button>

      <div v-if="pasteOpen" class="pal-paste" data-field="pal-paste">
        <textarea
          class="pal-paste-area"
          data-field="pal-paste-area"
          rows="3"
          spellcheck="false"
          placeholder="#6366f1 #0ea5e9 #f43f5e —— 空格 / 逗号 / 换行分隔都行，也认整段 CSS"
          :value="pasteText"
          @input="pasteText = ($event.target as HTMLTextAreaElement).value"
        />
        <div class="pal-paste-foot">
          <span class="pal-paste-found" data-field="pal-paste-found">
            <template v-if="pasteFound.length > 0">
              <span
                v-for="color in pasteFound.slice(0, PREVIEW_MAX)"
                :key="color.hex"
                class="pal-paste-swatch"
                :style="{ background: color.hex }"
                :title="`${color.name} ${color.hex}`"
              />
              <b class="pal-paste-count" data-field="pal-paste-count">{{ pasteFound.length }} 个颜色</b>
            </template>
            <span v-else class="panel-note">还没认出颜色</span>
          </span>
        </div>
        <div class="pal-paste-acts">
          <button class="ghost-btn" data-field="pal-paste-cancel" @click="togglePaste">取消</button>
          <button
            class="primary-btn"
            data-field="pal-paste-save"
            :disabled="pasteFound.length === 0"
            @click="savePaste"
          >
            导入到品牌色板
          </button>
        </div>
      </div>
    </section>

    <p class="panel-note pal-hint" data-field="pal-hint">
      {{
        palette.hint ||
          (mappedCount
            ? `${mappedCount} 种颜色已映射，点「重新应用」写入文档`
            : '选色即写入文档（Ctrl+Z 可撤回）· 悬停行可预览它的元素 · 点行右侧色块选色')
      }}
    </p>

    <!-- 悬浮框：Teleport 到 body，避免被面板的 overflow / 层叠上下文裁切 -->
    <Teleport to="body">
      <div
        v-if="palette.pickerFor"
        class="pal-pop"
        data-field="pal-pop"
        :style="{ left: `${popAt.left}px`, top: `${popAt.top}px`, width: `${popAt.width}px` }"
      >
        <div class="pal-pop-row">
          <span class="pal-pop-gname">品牌色板</span>
          <div class="pal-pop-grid">
            <button
              v-for="color in brandColors()"
              :key="color.id"
              class="pal-pop-swatch"
              :class="{ 'is-active': targetOf(palette.pickerFor) === color.hex }"
              data-field="pal-pop-brand"
              :data-hex="color.hex"
              :style="{ background: color.hex }"
              :title="`${color.name} ${color.hex}`"
              @click="choose(color.hex)"
            />
          </div>
        </div>
        <div class="pal-pop-div" />
        <!-- 内置预设配色组：只读的公共方案，点一下即映射（不往品牌色板里塞东西） -->
        <div v-for="preset in COLOR_PRESETS" :key="preset.id" class="pal-pop-row" :data-group="preset.id">
          <span class="pal-pop-gname" :title="preset.name">{{ preset.name }}</span>
          <div class="pal-pop-grid">
            <button
              v-for="color in preset.colors"
              :key="color.hex"
              class="pal-pop-swatch"
              :class="{ 'is-active': targetOf(palette.pickerFor) === color.hex }"
              data-field="pal-pop-preset"
              :data-hex="color.hex"
              :style="{ background: color.hex }"
              :title="`${preset.name} · ${color.name} ${color.hex}`"
              @click="choose(color.hex)"
            />
          </div>
        </div>
        <div class="pal-pop-div" />
        <div class="pal-pop-custom">
          <span>自定义颜色</span>
          <span class="grow" />
          <span class="pal-pop-hex" data-field="pal-pop-hex">{{ customHex }}</span>
          <input
            class="pal-colorinput"
            type="color"
            data-field="pal-pop-custom"
            :value="customHex"
            aria-label="自定义颜色"
            @input="onCustom"
          />
        </div>
        <button
          v-if="palette.pickerFor && targetOf(palette.pickerFor)"
          class="pal-pop-clear"
          data-field="pal-pop-clear"
          @click="clearRow"
        >
          清除这一行的映射
        </button>
      </div>
    </Teleport>
  </div>
</template>
